import React, { forwardRef } from "react";
import { motion, useTransform } from "framer-motion";
import {
  ArrowTopRightOnSquareIcon,
  ArrowUturnLeftIcon,
} from "@heroicons/react/16/solid";
import { Text } from "../tailwind/text";
import { BlockStack } from "@shopify/polaris";
import { formatParagraph } from "../../functions/formatParagraph";

const SPRING_OPTIONS = { type: "spring", stiffness: 300, damping: 30 };

const CarouselItem = forwardRef(function CarouselItem(
  { item, index, trackItemOffset, itemWidth, x, round, headerType, maxHeight },
  ref
) {
  const range = [
    -(index + 1) * trackItemOffset,
    -index * trackItemOffset,
    -(index - 1) * trackItemOffset,
  ];
  const outputRange = [90, 0, -90];
  const rotateY = useTransform(x, range, outputRange, { clamp: false });

  let lastValidVideoUrl = null;
  const Header = ({ headerLink }) => {
    if (headerType.toLowerCase() === "image") {
      const source = headerLink
        ? headerLink
        : "https://firebasestorage.googleapis.com/v0/b/phocea-75b47.appspot.com/o/Compa%2Fph_image.png?alt=media&token=b42e2452-09c4-4076-9956-15520363da4b";
      return (
        <div className="w-full flex justify-center mb-2 p-1 rounded-customBorderRadius h-[200px] overflow-hidden">
          <img
            alt="En-tête de la campagne"
            src={source}
            width="350px"
            height="200px"
            style={{
              objectFit: "cover",
              objectPosition: "center 32%",
              borderRadius: "10px",
            }}
          />
        </div>
      );
    }
    if (headerType.toLowerCase() === "video") {
      if (headerLink) {
        lastValidVideoUrl = headerLink;
      }
      const fallbackSource =
        "https://firebasestorage.googleapis.com/v0/b/phocea-75b47.appspot.com/o/Compa%2Fph_video.png?alt=media&token=33b76a14-ff16-41b0-acfb-48b1159ba993";
      const source = lastValidVideoUrl || fallbackSource;

      if (headerLink) {
        return (
          <div className="w-full flex justify-center mb-2 p-1 rounded-customBorderRadius h-[200px] overflow-hidden">
            <video
              alt="En-tête de la campagne"
              key={source}
              src={source}
              width="350px"
              height="200px"
              style={{
                objectFit: "cover",
                objectPosition: "center 32%",
                borderRadius: "10px",
              }}
            />
          </div>
        );
      }
      return (
        <div className="w-full flex justify-center mb-2 p-1 rounded-customBorderRadius h-[200px] overflow-hidden">
          <img
            alt="En-tête de la campagne"
            src={source}
            width="350px"
            height="200px"
            style={{
              objectFit: "cover",
              objectPosition: "center 32%",
              borderRadius: "10px",
            }}
          />
        </div>
      );
    }
    return null;
  };

  const containerStyle = {
    width: itemWidth,
    height: round ? itemWidth : maxHeight ? `${maxHeight}px` : "auto",
    rotateY: rotateY,
    ...(round && { borderRadius: "50%" }),
  };

  return (
    <motion.div
      ref={ref}
      data-carousel-item
      className={`relative shrink-0 flex flex-col justify-between ${
        round
          ? "items-center justify-center text-center border"
          : "items-center justify-center border rounded-customBorderRadius"
      } overflow-hidden cursor-grab active:cursor-grabbing`}
      style={containerStyle}
      transition={SPRING_OPTIONS}
    >
      <div className={round ? "p-0 m-0" : "p-0"}>
        <Header headerLink={item.headerLink} />
      </div>
      <div className="p-2 w-[97%] text-left template-preview mx-[-0.5rem] mt-[-0.5rem] flex-1 overflow-hidden">
        <BlockStack gap="3">
          {formatParagraph(item.body).map((formattedLine, index) => (
            <Text as="div" key={index}>
              {formattedLine}
            </Text>
          ))}
        </BlockStack>
      </div>
      <div className="buttons-preview w-[97%]">
        {item.buttons.map((button, index) => (
          <div className="button-preview my-1" key={`${button.text}-${index}`}>
            <div className="flex items-center">
              {button.type.toLowerCase() === "url" ? (
                <ArrowTopRightOnSquareIcon className="mr-1 h-[16px]" />
              ) : (
                <ArrowUturnLeftIcon className="mr-1 h-[16px]" />
              )}
              <Text className="text-center w-full text-[#31795c]">
                {button.text}
              </Text>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
});

export default CarouselItem;
